import { Component, inject, OnInit, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';

import { Auth } from '@core/services';
import { Title, Toaster } from '@shared/services';
import { Message as MessageService } from '@modules/message/services/message';

@Component({
  selector: 'app-message',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './message.html',
  styleUrl: './message.scss'
})
export class Message implements OnInit {
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private authService = inject(Auth);
  private titleService = inject(Title);
  private toasterService = inject(Toaster);
  private messageService = inject(MessageService);

  packages = signal<any[]>([]);
  charCount = 0;
  messageCount = 0;
  messageCost = 0;

  messageForm: FormGroup = this.fb.group({
    number: ['', [Validators.required, Validators.pattern('^[0-9]{10,15}$')]],
    package: [this.packages()[0], [Validators.required]],
    senderId: ['', [Validators.required]],
    messageText: ['', [Validators.required, Validators.maxLength(1000)]]
  });

  user = computed(() => this.authService.user || {});
  senderIds = computed(() => this.user().senderId);

  ngOnInit() {
    this.titleService.setTitle('Message');
    this.getPackages();
    // Listen for message text changes to update counts
    this.messageForm.get('messageText')?.valueChanges.subscribe((value) => {
      this.updateCounts(value || '');
    });
    this.messageForm.patchValue({ senderId: this.senderIds()[0] });
  }

  getPackages() {
    this.messageService.packageList({}).subscribe({
      next: (response: { data: any }) => {
        this.packages.set(response.data);
        this.messageForm.patchValue({ package: this.packages()[0] });
      },
      error: (error) => {
        this.toasterService.errorAlert(error.error.message);
      }
    });
  }

  updateCounts(text: string) {
    this.charCount = text.length;
    const gsmBody = {
      message: text
    };
    this.messageService.validateGSM(gsmBody).subscribe({
      next: (response: { data: any }) => {
        this.messageCount = response.data.totalMessaages;
        if (this.messageForm.get('package')?.value) {
          const selectedPkg = this.messageForm.get('package')?.value;
          this.messageCost = Number(this.messageCount) * Number(selectedPkg.price);
        } else {
          this.messageCost = 0;
        }
      },
      error: (error) => {
        this.toasterService.errorAlert(error.error.message);
      }
    });
  }

  selectPackage(pkg: any) {
    this.messageForm.patchValue({ package: pkg });
  }

  selectSenderId(id: string) {
    this.messageForm.patchValue({ senderId: id });
  }

  onSubmit() {
    if (this.messageForm.invalid) {
      this.toasterService.errorAlert('Please fill in all required fields correctly');
      return;
    }

    const data: any = {
      number: this.messageForm.get('number')?.value,
      package: this.messageForm.get('package')?.value.name,
      senderId: this.messageForm.get('senderId')?.value,
      text: this.messageForm.get('messageText')?.value
    };
    this.messageService.sendMessage(data).subscribe({
      next: () => {
        this.toasterService.successAlert('Message sent successfully');
        this.router.navigate(['/history']);
      },
      error: (error) => {
        this.toasterService.errorAlert(error.error.message);
      }
    });
  }
}
